import { FundOfferBaseEntity } from '@genial/services';
import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import * as zod from 'zod';

import type { FundRedemptionOfferNormalized } from '../../../../../store/product/types';
import { productsDetailText } from '../../../constants';
import { ModalFundsDetailsParamsProps } from '../../../models';
import { handleOrderByProps } from '../../constants';
import { messageError } from '../Equity/constants';

type FundsTableKey =
  | 'productDetails'
  | 'productName'
  | 'productCategory'
  | 'profitabilityTwelveMonthsAdjusted'
  | 'administrationFee'
  | 'quotationRedemptionTermFormatted'
  | 'riskRatingLevel'
  | 'inputApplicationAmount'
  | 'buttonSelectProduct';

type FundRedemptionTableKey =
  | 'productName'
  | 'totalRedemption'
  | 'amountToRedemption'
  | 'estimatedIncomeTax'
  | 'estimatedNetValue'
  | 'quotationDate'
  | 'buttonSelectProduct';

export type FundsTableItemProps = Record<FundsTableKey, string> &
  Partial<{
    qualifiedInvestor: boolean;
    professionalInvestor: boolean;
    fundStatusId: number;
    externalId: number;
    riskRating: string;
    productId: string;
    fundCnpj: string;
    minimumMovimentation: number;
    isReaplication: boolean;
  }>;

export type FundRedemptionTableItemProps = Record<
  FundRedemptionTableKey,
  string | number | boolean
> & {
  productId: string;
  quotationDateFormatted: string;
};

export type Offer = FundOfferBaseEntity & FundRedemptionOfferNormalized;

export interface FundsTableProps {
  OffersAll?: Offer[];
  OffersSearchTerm?: Offer[];
  onOpenDetailsModal: (data: ModalFundsDetailsParamsProps) => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  handleEndReached: () => void;
  onChangeMessageAdjustUnitPrice: (value: boolean) => void;
  marketIsClosed: boolean;
  marketStatus?: string;
  invalidColumns?: boolean;
  handleClearSearch?: () => void;
  handleOrderBy: handleOrderByProps;
  hasEmpty: boolean;
}

export const tableHead: FundsTableItemProps = {
  productDetails: '',
  productName: 'Produto',
  productCategory: 'Tipo',
  profitabilityTwelveMonthsAdjusted: 'Rent. 12 meses',
  administrationFee: 'Taxa admin.',
  quotationRedemptionTermFormatted: 'Resgate',
  riskRatingLevel: 'Risco',
  inputApplicationAmount: 'Valor a aplicar',
  buttonSelectProduct: '',
};

export const tableRedemptionHead: Partial<FundRedemptionTableItemProps> = {
  productName: 'Produto',
  totalRedemption: 'Resgate total',
  amountToRedemption: 'Valor a resgatar',
  estimatedIncomeTax: 'IR estimado',
  estimatedNetValue: 'Valor líq. estimado',
  quotationDate: 'Data cotização',
  buttonSelectProduct: '',
};

export const orderByData = [
  {
    label: 'Produto',
    value: 'productName',
  },
  {
    label: 'Tipo',
    value: 'productCategory',
  },
  {
    label: 'Rent. 12 meses',
    value: 'profitabilityTwelveMonthsAdjusted',
  },
  {
    label: 'Taxa admin.',
    value: 'administrationFee',
  },
  {
    label: 'Resgate',
    value: 'quotationRedemptionTermFormatted',
  },
  {
    label: 'Risco',
    value: 'riskRatingLevel',
  },
];

export const selectedKeysOrderBy = orderByData.map((item) => item.value);

export const tableParse = (item: Offer) => {
  return {
    ...item,
    productDetails: productsDetailText,
    profitabilityTwelveMonthsAdjusted: Utils.Mask.Percentage(
      item.profitabilityTwelveMonthsAdjusted
    ),
    fundCnpj: item.fundCnpj || '',
    productId: item.productId?.toString(),
    riskRating: item.riskRating || '',
    productCategory: item.productCategory || '',
    productName: item.productName || '',
    administrationFee: item.administrationFee as string, // Corrigir campo muito grande
    quotationRedemptionTermFormatted: item.quotationRedemptionTermFormatted as string,
    riskRatingLevel: String(item.riskRatingLevel || 0),
    inputApplicationAmount: Utils.Mask.Money(item.applicationMinimumAmount),
    minimumMovimentation: item.hasPosition
      ? (item.applicationMinimumAmount as number)
      : (item.participateMinimumAmount as number),
    isReaplication: item.hasPosition as boolean,
    buttonSelectProduct: item.hasPosition ? 'Reaplicar' : 'Investir',
  } as Partial<FundsTableItemProps>;
};

export const tableParseRedemption = (item: Offer) => {
  return {
    ...item,
    productName: item.fundName || messageError,
    productId: item.productId || messageError,
    amountToRedemption: item.grossTotal || 0,
    expectedIR: item.expectedIR || 0,
    estimatedIncomeTax: Utils.Mask.Money(item.expectedIR || 0),
    estimatedNetValue: Utils.Mask.Money(item.netTotal || 0),
    quotationDateFormatted: Utils.Mask.DateFormat(item.quotationDate, 'dd/MM/yyyy'),
    liquidationDateFormatted: Utils.Mask.DateFormat(item.liquidationDate, 'dd/MM/yyyy'),
    buttonSelectProduct: 'RESGATAR',
  } as unknown as FundRedemptionTableItemProps & FundRedemptionOfferNormalized;
};

export const widthCols: Record<FundsTableKey, number> = {
  productDetails: 4,
  productName: 16,
  productCategory: 11,
  profitabilityTwelveMonthsAdjusted: 11,
  administrationFee: 11,
  quotationRedemptionTermFormatted: 8,
  riskRatingLevel: 10,
  inputApplicationAmount: 16,
  buttonSelectProduct: 13,
};

export const fundRedemptionWidthCols: Record<FundRedemptionTableKey, number> = {
  productName: 20,
  totalRedemption: 10,
  amountToRedemption: 14,
  estimatedIncomeTax: 10,
  estimatedNetValue: 16,
  quotationDate: 16,
  buttonSelectProduct: 14,
};

export const invalidColumnsCalc =
  Object.entries(widthCols).reduce((acc, item) => acc + item[1], 0) !== 100;

export type FormData = { createAppliedAmount: string };

export const validateCreateAppliedAmount = (min: number, max: number) => {
  const validationCreateSchema = zod.object({
    createAppliedAmount: zod
      .string({ required_error: `Valor mínimo ${Utils.Mask.Money(min)}` })
      .refine((value) => Utils.Unmask.Money(value) >= min, {
        message: `Valor mínimo ${Utils.Mask.Money(min)}`,
      })
      .refine((value) => Utils.Unmask.Money(value) <= max, {
        message: `Valor máximo ${Utils.Mask.Money(max)}`,
      }),
  });

  return zodResolver(validationCreateSchema);
};

export const validateFundCreateAppliedAmount = (min: number) => {
  const validationCreateSchema = zod.object({
    createAppliedAmount: zod
      .string({ required_error: `Valor mínimo ${Utils.Mask.Money(min)}` })
      .refine((value) => Utils.Unmask.Money(value) >= min, {
        message: `Valor mínimo ${Utils.Mask.Money(min)}`,
      }),
  });

  return zodResolver(validationCreateSchema);
};

export const createArrayWithBooleans = (number: number | string) => {
  const value = Number(number);
  const array = new Array(5).fill(false);
  const result = array.map((_, index) => index < value);

  return result;
};

export type FundOperationType = 'apply' | 'rescue';

export const normalizeRequestDate = (date: Date): Date => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  return normalizedDate;
};
